// slices/customerSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { CustomerDetails } from 'generated-api/bff/api';
import { getApiInstance } from 'services/Api';

// Async thunk for fetching customer details
export const fetchCustomerDetails = createAsyncThunk<CustomerDetails>('customer/fetchCustomerDetails', async () => {
	const apiInstance = await getApiInstance('/commercial-selfservice/');
	const response = await apiInstance.bffApiServiceLayer.getCustomerDetails();

	if (response.status === HttpStatusCode.NoContent) {
		return;
	}

	if (!response.data) {
		throw new Error('No customer data found');
	}
	return response.data;
});

interface CustomerState {
	data: CustomerDetails | null;
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: CustomerState = {
	data: null,
	status: HttpStatusCode.Idle,
	error: null,
};

// Slice for customer
const customerSlice = createSlice({
	name: 'customer',
	initialState,
	reducers: {
		clearCustomerCache(state) {
			state.data = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchCustomerDetails.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchCustomerDetails.fulfilled, (state, action) => {
				if (!action.payload) {
					state.status = HttpStatusCode.NoContent;
					return;
				}

				state.status = HttpStatusCode.OK;
				state.data = action.payload;
			})
			.addCase(fetchCustomerDetails.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to fetch customer data';
			});
	},
});

export const { clearCustomerCache } = customerSlice.actions;
export default customerSlice.reducer;
